import React, { useEffect, useState } from 'react';

const palette = {
  background: '#f7f3eb',
  text: '#112240',
  muted: '#4a6076',
  accent: '#c58a1e',
  highlight: '#1e3a8a',
  badgeBorder: 'rgba(17, 34, 64, 0.1)',
  card: '#ffffff',
  softShadow: '0 32px 56px rgba(17, 34, 64, 0.14)',
  support: '#eef3f8',
};

const baseFont = "Inter, -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Oxygen, Ubuntu, Cantarell, 'Fira Sans', 'Droid Sans', 'Helvetica Neue', Arial, sans-serif";

const styles = {
  page: {
    minHeight: '100vh',
    width: '100%',
    margin: 0,
    boxSizing: 'border-box',
    background: 'linear-gradient(180deg, #fbf7f0 0%, #efe4d6 100%)',
    color: palette.text,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'stretch',
    fontFamily: baseFont,
  },
  cardContainer: {
    flex: '1 1 auto',
    width: '100%',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    padding: '48px 24px 64px',
  },
  card: {
    width: '100%',
    maxWidth: '720px',
    background: palette.card,
    border: `1px solid rgba(17, 34, 64, 0.08)`,
    borderRadius: '16px',
    padding: '28px',
    boxShadow: palette.softShadow,
    position: 'relative',
    overflow: 'hidden',
    textAlign: 'left',
  },
  cardAccent: {
    position: 'absolute',
    inset: 0,
    borderRadius: 'inherit',
    background: 'linear-gradient(135deg, rgba(197,138,30,0.15) 0%, rgba(30,58,138,0.22) 100%)',
    opacity: 0.24,
  },
  cardInner: {
    position: 'relative',
  },
  headerBar: {
    width: '100%',
    background: '#ffffff',
    borderBottom: '1px solid rgba(17,34,64,0.12)',
    boxShadow: '0 8px 20px rgba(17,34,64,0.08)',
    padding: '16px 28px',
    display: 'flex',
    alignItems: 'center',
    gap: '32px',
    justifyContent: 'flex-start',
    minHeight: '73px',
  },
  headerLogo: {
    width: '180px',
    height: 'auto',
    display: 'block',
  },
  headerNav: {
    display: 'flex',
    alignItems: 'center',
    gap: '24px',
    flexWrap: 'wrap',
    flex: 1,
    justifyContent: 'flex-start',
    marginLeft: '100px',
  },
  headerLink: {
    color: '#1b2d4b',
    textDecoration: 'none',
    fontSize: '13px',
    fontWeight: 700,
    letterSpacing: '0.12em',
    textTransform: 'uppercase',
  },
  portalLabel: {
    fontSize: '14px',
    fontWeight: 600,
    letterSpacing: '0.1em',
    textTransform: 'uppercase',
    color: palette.highlight,
    marginBottom: '12px',
  },
  eyebrow: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '8px',
    fontSize: '12px',
    fontWeight: 600,
    letterSpacing: '0.08em',
    textTransform: 'uppercase',
    color: palette.muted,
  },
  dot: {
    width: '8px',
    height: '8px',
    borderRadius: '50%',
    background: palette.accent,
    boxShadow: '0 0 0 6px rgba(197,138,30,0.18)',
  },
  heading: {
    margin: '12px 0 8px',
    fontSize: 'clamp(22px, 5vw, 28px)',
    lineHeight: 1.2,
  },
  bodyText: {
    margin: 0,
    color: palette.text,
    fontSize: '18px',
    lineHeight: 1.7,
  },
  meta: {
    marginTop: '18px',
    fontSize: '16px',
    color: palette.muted,
  },
  divider: {
    height: '1px',
    background: 'rgba(17,34,64,0.14)',
    border: 'none',
    margin: '20px 0',
  },
  support: {
    marginTop: '24px',
    padding: 0,
    background: 'transparent',
    borderRadius: '12px',
    border: '1px solid transparent',
  },
  supportHeading: {
    margin: '0 0 12px',
    fontSize: '18px',
    fontWeight: 600,
    color: palette.text,
  },
  supportList: {
    margin: 0,
    padding: 0,
    listStyle: 'none',
    color: palette.text,
    fontSize: '17px',
    lineHeight: 1.7,
  },
  supportItem: {
    marginBottom: '8px',
  },
  contactLink: {
    color: '#7B569B',
    textDecoration: 'none',
    fontWeight: 600,
  },
  mobileNavOverlay: {
    position: 'fixed',
    top: 0,
    left: 0,
    width: '100%',
    height: '100%',
    background: '#ffffff',
    color: '#1b2d4b',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-start',
    padding: '24px',
    zIndex: 9999,
  },
  mobileNavClose: {
    alignSelf: 'flex-end',
    background: 'transparent',
    border: 'none',
    color: '#1b2d4b',
    fontSize: '28px',
    cursor: 'pointer',
    marginBottom: '24px',
  },
  mobileNavLinks: {
    display: 'flex',
    flexDirection: 'column',
    gap: '18px',
    width: '100%',
  },
  mobileNavLink: {
    color: '#1b2d4b',
    textDecoration: 'none',
    fontSize: '18px',
    fontWeight: 700,
    letterSpacing: '0.08em',
    textTransform: 'uppercase',
  },
  mobileNavTrigger: {
    alignItems: 'center',
    justifyContent: 'center',
    width: '44px',
    height: '44px',
    borderRadius: '999px',
    background: 'transparent',
    color: '#1b2d4b',
    fontWeight: 700,
    fontSize: '18px',
    border: 'none',
    cursor: 'pointer',
    transition: 'color 0.2s ease-in-out',
  },
};

const portalLogo = 'https://investor.avgfunds.com/images/logo_new.png';
const headerLinks = [
  { label: 'Funds', href: 'https://www.av.vc/funds' },
  { label: 'Syndicates', href: 'https://www.av.vc/syndicates' },
  { label: 'Portfolio', href: 'https://www.av.vc/portfolio' },
  { label: 'Content', href: 'https://www.av.vc/content' },
  { label: 'About Us', href: 'https://www.av.vc/about' },
  { label: 'Entrepreneurs', href: 'https://www.av.vc/entrepreneurs' },
];

const Maintenance = () => {
  const [mobileNavOpen, setMobileNavOpen] = useState(false);
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    const checkViewport = () => {
      if (typeof window !== 'undefined') {
        setIsMobile(window.innerWidth <= 1024);
      }
    };
    checkViewport();
    if (typeof window !== 'undefined') {
      window.addEventListener('resize', checkViewport);
    }
    return () => {
      if (typeof window !== 'undefined') {
        window.removeEventListener('resize', checkViewport);
      }
    };
  }, []);

  useEffect(() => {
    if (!isMobile && mobileNavOpen) {
      setMobileNavOpen(false);
    }
  }, [isMobile, mobileNavOpen]);

  const responsiveHeaderStyles = `
    .maintenance-header-mobile-trigger {
      display: none;
    }
    @media (max-width: 1024px) {
      .maintenance-header-bar {
        flex-wrap: nowrap;
        justify-content: space-between;
        padding: 14px 20px;
        gap: 12px;
        min-height: 73px;
      }
      .maintenance-header-bar img {
        margin-bottom: 0;
        width: 140px;
      }
      .maintenance-header-nav {
        display: none !important;
      }
      .maintenance-header-mobile-trigger {
        display: inline-flex;
        position: fixed;
        top: 16px;
        right: 20px;
        z-index: 10000;
      }
    }
    @media (min-width: 1025px) {
      .maintenance-header-mobile-trigger {
        display: none;
      }
    }
  `;

  useEffect(() => {
    const previousTitle = document.title;
    document.title = 'Status - Alumni Ventures';
    return () => {
      document.title = previousTitle;
    };
  }, []);

  return (
    <div style={styles.page}>
      <style>{responsiveHeaderStyles}</style>
      <header style={styles.headerBar} className="maintenance-header-bar">
        <img src={portalLogo} alt="Alumni Ventures" style={styles.headerLogo} />
        {!isMobile && (
          <nav style={styles.headerNav} className="maintenance-header-nav" aria-label="Alumni Ventures">
            {headerLinks.map((link) => (
              <a
                key={link.href}
                href={link.href}
                style={styles.headerLink}
                target="_blank"
                rel="noreferrer"
              >
                {link.label}
              </a>
            ))}
          </nav>
        )}
        {isMobile && (
          <button
            type="button"
            className="maintenance-header-mobile-trigger"
            aria-label="Open navigation menu"
            aria-expanded={mobileNavOpen}
            onClick={() => setMobileNavOpen(true)}
            style={{
              ...styles.mobileNavTrigger,
              ...(mobileNavOpen ? styles.mobileNavTriggerActive : {}),
            }}
          >
            ☰
          </button>
        )}
      </header>
      {mobileNavOpen && isMobile && (
        <div style={styles.mobileNavOverlay} role="dialog" aria-modal="true">
          <button
            type="button"
            style={styles.mobileNavClose}
            aria-label="Close navigation menu"
            onClick={() => setMobileNavOpen(false)}
          >
            ×
          </button>
          <div style={styles.mobileNavLinks}>
            {headerLinks.map((link) => (
              <a
                key={link.href}
                href={link.href}
                style={styles.mobileNavLink}
                target="_blank"
                rel="noreferrer"
                onClick={() => setMobileNavOpen(false)}
              >
                {link.label}
              </a>
            ))}
          </div>
        </div>
      )}
      <div style={styles.cardContainer}>
        <main style={styles.card} role="main" aria-label="Service status">
          <div style={styles.cardAccent} aria-hidden="true" />
          <div style={styles.cardInner}>
            <div style={styles.eyebrow} aria-live="polite">
              <span style={styles.dot} aria-hidden="true" />
              <span>Service Notice</span>
            </div>
            <h1 style={styles.heading}>Service Outage Notice</h1>
            <p style={styles.bodyText}>
              We are experiencing a temporary service disruption due to infrastructure issues with a third party service provider.
              Our team is working with our partners to restore full service availability.
            </p>
            <p style={styles.meta}>
              Thank you for your patience. We will continue to post updates until the outage is resolved.
            </p>

            <hr style={styles.divider} />
            <p style={styles.meta}>
              For immediate assistance, please contact our Investor Relations team at
              {' '}
              <a
                href="mailto:investor.relations@avgfunds.com"
                style={styles.contactLink}
              >
                investor.relations@av.vc
              </a>
              .
            </p>
          </div>
        </main>
      </div>
    </div>
  );
};

export default Maintenance;
